import { Button, CircularProgress, Tooltip } from '@mui/material';
import { Grid } from '@mui/material';
import { INVESTOR_REPORTING } from 'adminConstants/routes';
import { DownloadPdfUpdateButton } from 'components/DownloadPdfUpdateButton';
import { AppSelectBox, AppTextInput } from 'components/FormElements';
import GridContainer from 'components/GridContainer';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import Table from 'components/Table';
import { useInvestorReportingPdfBulkDownload } from 'hooks/data/investorReporting/useBulkDownloadInvestorReportingPdf';
import { useGetInvestorsInReport } from 'hooks/data/investorReporting/useGetInvestorsInReport';
import { useInvestorReporting } from 'hooks/data/investorReporting/useInvestorReporting';
import { usePagination } from 'further-ui/hooks';
import { orderBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { useDebounce } from 'usehooks-ts';
import { checkPdfsAreDownloadable } from '../helpers/check-pdfs-are-downloadable';
import { SortOrder } from 'further-types/utils';

const useStyles = makeStyles()(() => ({
  gridMargin: {
    marginBottom: '1rem',
  },
  downloadBtn: {
    display: 'flex',
    justifyContent: 'flex-end',
    height: '4.5rem',
  },
}));

const InvestorReportingPdfDownload = () => {
  const { id: reportingId } = useParams<{ id: string }>();
  const [search, setSearch] = useState('');
  const [isHardCopyOnly, setIsHardCopyOnly] = useState(false);
  const pagination = usePagination({
    order: SortOrder.Asc,
    orderBy: 'fullName',
    pageSize: 25,
  });
  const debouncedSearchTerm = useDebounce(search, 300);
  const investors = useGetInvestorsInReport({ params: { reportingId } });
  const { data: investorReportData } =
    useInvestorReporting().fetchInvestorReport(reportingId);

  const { classes } = useStyles();

  const columns = useMemo(
    () => [
      {
        label: 'Name',
        key: 'fullName',
      },
      {
        label: 'Email',
        key: 'email',
      },
      {
        label: 'External Investor ID',
        key: 'externalInvestorId',
      },
      {
        label: 'Download',
        key: 'download',
        sort: false,
        render: (elm) => (
          <DownloadPdfUpdateButton
            reportingId={reportingId}
            investorId={elm._id}
            filename={`${elm.fullName} ${investorReportData?.investorReport?.managerOverViewTitle} investor update.pdf`}
            pdfUrl={elm.reportPdfUrl}
          />
        ),
      },
    ],
    [reportingId, investorReportData?.investorReport?.managerOverViewTitle],
  );

  useEffect(() => {
    pagination.toFirstPage();
  }, [debouncedSearchTerm, isHardCopyOnly]);

  const handleFilters = useCallback(
    (investor) => {
      // No Filters Applied
      if (!debouncedSearchTerm && !isHardCopyOnly) return true;

      const term = debouncedSearchTerm?.toLowerCase() ?? '';
      return (
        (!debouncedSearchTerm ||
          investor?.fullName?.toLowerCase().includes(term) ||
          investor?.email?.toLowerCase().includes(term) ||
          investor?.externalInvestorId?.toLowerCase().includes(term)) &&
        (!isHardCopyOnly || investor.requiresHardCopyReports)
      );
    },
    [debouncedSearchTerm, isHardCopyOnly],
  );

  const filteredTablebody = useMemo(
    () =>
      orderBy(
        investors?.data?.investors?.filter(handleFilters) ?? [],
        [pagination.orderBy],
        [pagination.order],
      ),
    [
      investors.data,
      debouncedSearchTerm,
      pagination.orderBy,
      pagination.order,
      isHardCopyOnly,
    ],
  );

  const paginatedTablebody = useMemo(
    () =>
      filteredTablebody.slice(
        (pagination.page - 1) * pagination.rowsPerPage,
        pagination.rowsPerPage * pagination.page,
      ),
    [
      filteredTablebody,
      pagination.page,
      pagination.rowsPerPage,
      isHardCopyOnly,
    ],
  );

  const investorOptions = [
    {
      key: false,
      label: 'All investors',
    },
    {
      key: true,
      label: 'Investors requiring hard copies',
    },
  ];

  const areAllPdfDownloadable = checkPdfsAreDownloadable({
    buildingPdfStatus: investorReportData?.investorReport?.buildingPdfStatus,
    createdAt: investorReportData?.investorReport?.createdAt,
  });

  const bulkDownload = useInvestorReportingPdfBulkDownload(
    reportingId,
    investorReportData?.investorReport?.managerOverViewTitle,
    filteredTablebody.map(({ _id, fullName }) => {
      return { _id, fullName };
    }),
  );

  const isDownloadAllButtonDisabled =
    !areAllPdfDownloadable ||
    !filteredTablebody.length ||
    bulkDownload.isPending;

  return (
    <PageContainer
      heading="Investor Reporting: Download PDFs"
      breadcrumbs={[
        { label: 'Dashboard', link: '/' },
        { label: 'Investor Reporting', link: INVESTOR_REPORTING },
        { label: 'Download PDFs', isActive: true, withoutFormatting: true },
      ]}
    >
      <PageContent>
        <GridContainer className={classes.gridMargin}>
          <Grid
            size={{
              md: 4,
              xs: 12,
            }}
          >
            <AppTextInput
              name="searchInvestors"
              variant="outlined"
              placeholder="Filter by name, email or ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
          <Grid
            size={{
              md: 4,
              xs: 12,
            }}
          >
            <AppSelectBox
              data={investorOptions}
              valueKey="key"
              labelKey="label"
              value={isHardCopyOnly}
              variant="outlined"
              onChange={(event) => {
                const { value } = event.target;
                //@ts-expect-error
                setIsHardCopyOnly(value);
              }}
            />
          </Grid>

          {!investors.isFetching && (
            <Grid
              className={classes.downloadBtn}
              size={{
                md: 4,
                xs: 12,
              }}
            >
              <Tooltip
                title={
                  investorReportData?.investorReport?.buildingPdfStatus ===
                  'processing'
                    ? 'Bulk PDF updates will be available for download in 15-30 minutes'
                    : ''
                }
              >
                <span>
                  <Button
                    onClick={bulkDownload.mutate}
                    disabled={isDownloadAllButtonDisabled}
                    //@ts-expect-error
                    color={isDownloadAllButtonDisabled ? 'default' : 'primary'}
                    variant="contained"
                  >
                    {!bulkDownload.isPending ? (
                      'Download all'
                    ) : (
                      <CircularProgress color="inherit" size={24} />
                    )}
                  </Button>
                </span>
              </Tooltip>
            </Grid>
          )}
        </GridContainer>

        <Table
          pagination={true}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          count={filteredTablebody?.length ?? 0}
          columns={columns}
          tablebody={paginatedTablebody}
          variant="nohover"
          emptyMessage="No investors were included in this update."
          loading={investors.isFetching}
          order={pagination.order}
          orderBy={pagination.orderBy}
          onRequestSort={pagination.handleRequestSort}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
        />
      </PageContent>
    </PageContainer>
  );
};

export default InvestorReportingPdfDownload;
